// src/pages/dashboard/directorproducer/ManageRolesPage.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Button, Spinner, Alert, Badge } from 'react-bootstrap';
import { useNavigate, Link } from 'react-router-dom';
import { 
  FaEdit, 
  FaPlus, 
  FaClone, 
  FaTrash, 
  FaMapMarkerAlt, 
  FaBriefcase, 
  FaLayerGroup 
} from 'react-icons/fa';

import { fetchRoleListApi, deleteRoleApi } from '../../services/api';
import MetaTags from '../../components/MetaTags'; 
import DataTable from '../../components/DataTable'; 

const ManageRolesPage = () => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  // ✅ LOAD & MAP ROLES
  const loadRoles = useCallback(async () => {
    setLoading(true);
    setError('');
    
    try {
      const res = await fetchRoleListApi();
      // Handle fallback extraction for both API response styles
      const rolesData = res?.data?.data || res?.data || [];

      const mappedRoles = rolesData.map((role) => {
        let parsedRequestData = {};
        
        // Parse the JSON blob stored in request_data for detailed cloning
        if (role.request_data) {
          try {
            parsedRequestData = typeof role.request_data === 'string' 
              ? JSON.parse(role.request_data) 
              : role.request_data;
          } catch (e) {
            console.error("Error parsing request_data for role", role.id);
          }
        }

        return {
          ...parsedRequestData, // Spread details so clone/edit have all fields
          id: role.id,         // Local SQL Integer ID
          joget_role_id: role.joget_role_id,
          roleld: role.roleld || parsedRequestData.roleld || '---',
          roleName: role.role_name || role.roleName || parsedRequestData.roleName || 'N/A',
          roleCategory: role.role_category || role.roleCategory || parsedRequestData.roleCategory || 'N/A',
          auditionType: parsedRequestData.auditionType || role.audition_type || role.auditionType || 'N/A',
          roleLocation: role.shoot_location || role.roleLocation || parsedRequestData.roleLocation || 'N/A',
          
          // PRIORITY: Use Joined project name safely
          projectName: role.projectName || role['project.projectName'] || role.project?.project_name || parsedRequestData.projectName || '---',
          
          status: role.status || 'N/A',
          dateCreated: role.created_at || role.dateCreated || 0,
        };
      });

      // SORT: Latest first
      const sortedRoles = mappedRoles.sort((a, b) => new Date(b.dateCreated) - new Date(a.dateCreated));

      setRoles(sortedRoles);
    } catch (err) { 
      console.error(err);
      setError('Failed to load roles.'); 
    } finally { 
      setLoading(false); 
    }
  }, []);

  useEffect(() => { 
    loadRoles(); 
  }, [loadRoles]);

  // ✅ EDIT ROLE (Pass Local Integer ID to Route)
  const handleEdit = (localId) => {
    navigate(`/dashboard/post-roles/${localId}`);
  };

  // ✅ CLONE ROLE
  const handleClone = (role) => {
    const clonedRole = { ...role };
    delete clonedRole.id; // Remove PK so backend creates new
    delete clonedRole.joget_role_id; // Remove UUID so backend creates new
    clonedRole.roleName = `${role.roleName} (Clone)`;

    navigate('/dashboard/post-roles', { state: { roleToClone: clonedRole } });
  };

  // ✅ DELETE ROLE (Uses Local Integer ID)
  const handleDelete = async (localId, roleName) => {
    if (window.confirm(`Are you sure you want to delete the role "${roleName}"?`)) {
      try {
        await deleteRoleApi(localId);
        alert('Role deleted successfully!');
        loadRoles(); 
      } catch (err) { 
        alert('Failed to delete role.'); 
      }
    }
  };

  // ✅ COLUMNS FOR DATATABLE
  const columns = useMemo(() => [
    {
      accessorKey: 'index',
      header: '#',
      cell: (info) => <span className="opacity-50 text-white">{info.row.index + 1}</span>,
      enableSorting: false,
    },
    {
      accessorKey: 'roleName',
      header: 'Role Title',
      cell: (info) => <span className="fw-bold text-white">{info.getValue()}</span>,
    },
    {
      accessorKey: 'projectName',
      header: 'Project',
      cell: (info) => <span className="text-warning fw-bold">{info.getValue()}</span>,
    },
    {
      accessorKey: 'roleCategory',
      header: 'Category',
      cell: (info) => <span className="text-capitalize">{info.getValue()}</span>,
    },
    {
      accessorKey: 'auditionType',
      header: 'Mode',
      cell: (info) => <span className="small">{info.getValue()}</span>,
    },
    {
      accessorKey: 'roleLocation',
      header: 'Location',
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: (info) => (
        <Badge bg={info.getValue() === 'active' ? 'success' : 'danger'} className="text-uppercase">
          {info.getValue()}
        </Badge>
      ),
    },
    {
      id: 'actions',
      header: () => <div className="text-center">Actions</div>,
      enableSorting: false,
      cell: (info) => {
        const role = info.row.original;
        return (
          <div className="d-flex justify-content-center gap-2">
            <Button variant="link" className="p-0 text-info" title="Edit" onClick={() => handleEdit(role.id)}>
              <FaEdit />
            </Button>
            <Button variant="link" className="p-0 text-warning" title="Clone" onClick={() => handleClone(role)}>
              <FaClone />
            </Button>
            <Button variant="link" className="p-0 text-danger" title="Delete" onClick={() => handleDelete(role.id, role.roleName)}>
              <FaTrash />
            </Button>
          </div>
        );
      },
    }
  ], []);

  // ✅ MOBILE CARD FOR DATATABLE
  const renderMobileCard = (role) => (
    <div className="role-mobile-card mb-3 p-3 bg-dark rounded shadow-sm border-start border-3 border-danger" key={role.id}>
      <div className="d-flex justify-content-between align-items-start mb-2">
        <div>
           <h6 className="role-card-title text-white m-0 fw-bold">{role.roleName}</h6>
           <small className="text-warning fw-bold">{role.projectName}</small>
        </div>
        <Badge bg={role.status === 'active' ? 'success' : 'danger'} className="text-uppercase badge-9px">
          {role.status}
        </Badge>
      </div>
      
      <div className="role-card-body">
        <div className="info-grid text-white opacity-75 small mb-2 d-flex flex-wrap gap-2">
          <span><FaLayerGroup className="me-1 text-danger"/> {role.roleCategory}</span>
          <span><FaBriefcase className="me-1 text-info"/> {role.auditionType}</span>
          <span><FaMapMarkerAlt className="me-1 text-success"/> {role.roleLocation}</span>
        </div>
        
        <div className="mt-2 d-flex justify-content-end gap-4 border-top border-secondary pt-2 fs-5">
            <FaEdit className="text-info cursor-pointer" onClick={() => handleEdit(role.id)} />
            <FaClone className="text-warning cursor-pointer" onClick={() => handleClone(role)} />
            <FaTrash className="text-danger cursor-pointer" onClick={() => handleDelete(role.id, role.roleName)} />
        </div>
      </div>
    </div>
  );

  // ✅ LOADING VIEW
  if (loading) return (
    <div className="text-center p-5" style={{ backgroundColor: 'transparent' }}>
      <Spinner animation="border" variant="danger" />
      <p className="text-white mt-2">Loading Roles...</p>
    </div>
  );

  // ✅ MAIN RENDER
  return (
    <div className="manage-roles-page-wrapper p-2 p-md-3">
      <MetaTags title="Manage Roles" />
      
      <div className="page-header d-flex justify-content-between align-items-center mb-3">
        <h5 className="text-uppercase fw-bold text-white m-0">
          Manage <span style={{ color: 'var(--primary-color)' }}>Roles</span>
        </h5>
        
        <Button
          as={Link}
          to="/dashboard/post-roles"
          variant="danger"
          className="btn-compact-header fw-bold"
        >
          <FaPlus className="me-1" /> Post Role
        </Button>
      </div>

      {error && <Alert variant="danger" className="bg-dark text-danger border-danger py-2 small">{error}</Alert>}

      <DataTable 
        columns={columns} 
        data={roles} 
        renderMobileItem={renderMobileCard} 
        searchPlaceholder="Search roles or projects..." 
      />
    </div>
  );
};

export default ManageRolesPage;